/**
 * Object-params command handlers. No React. No positional arguments.
 * Callers go through `invoke()` in dispatch.ts so argument shape is normalized.
 */
import { NodeFactory } from '../services/node/NodeFactory';
import { LayoutService } from '../services/layout/LayoutService';
import { saveToJSON, loadFromJSON } from '../utils/serializer';
import { mermaidToDiagram, diagramToMermaid, diagramToMermaidSequence } from '../utils/mermaidExchange';
import { computeDiagramContentBounds } from '../utils/contentBounds';
import { autoLayout } from '../utils/elkLayout';
import { CANVAS } from '../constants';
import { useSettingsStore } from '../stores/settingsStore';
import { estimateSequenceMessageFootprint } from '../utils/sequenceMessageLayout';
import { findSmartPosition } from '../utils/chords';
import { exportAllPagesToPng, exportCurrentPageToPng } from '../utils/pngExporter';
import { buildAIContext } from '../services/ai/contextBuilder';
import { useDiagramStore } from '../stores/diagramStore';
import { countCatalogViews } from '../utils/catalog';
import type { C4Level, CatalogComponent, DiagramType, Node, NodeMeta, SequenceMessageType } from '../types';
import type { CommandHandler } from './types';
import {
  asNumber,
  asPoint,
  asString,
  asStringArray,
  isResolveError,
  resolveNode,
  resolveParticipant,
  resolveSocket,
} from './resolve';

function ensureActiveDiagramType(type: DiagramType, newPageName: string, preferExisting: boolean): void {
  const state = useDiagramStore.getState();
  if (state.diagramType === type) return;
  if (preferExisting) {
    const existing = state.pages.find(page => (page.diagramType ?? 'flow') === type);
    if (existing) {
      state.switchPage(existing.id);
      return;
    }
  }
  const pageId = useDiagramStore.getState().addPage(newPageName, type);
  useDiagramStore.getState().switchPage(pageId);
}

function fitActiveFlowInWindow() {
  const st = useDiagramStore.getState();
  const bounds = computeDiagramContentBounds({
    nodes: st.nodes,
    edges: st.edges,
    groups: st.groups,
    participants: st.participants,
    messages: st.messages,
    fragments: st.fragments,
    seqLifelineLength: st.seqLifelineLength,
  });
  if (!bounds) return;
  const pad = 80;
  const width = typeof window === 'undefined' ? 1400 : window.innerWidth;
  const height = typeof window === 'undefined' ? 900 : window.innerHeight;
  const contentW = Math.max(1, bounds.maxX - bounds.minX);
  const contentH = Math.max(1, bounds.maxY - bounds.minY);
  const scale = Math.max(
    CANVAS.SCALE_MIN,
    Math.min(1, Math.min((width - pad * 2) / contentW, (height - pad * 2) / contentH)),
  );
  st.updateViewport({
    x: width / 2 - ((bounds.minX + bounds.maxX) / 2) * scale,
    y: height / 2 - ((bounds.minY + bounds.maxY) / 2) * scale,
    scale,
  });
}

function defaultNodePosition(size: { width: number; height: number }): { x: number; y: number } {
  const { nodes, groups } = useDiagramStore.getState();
  if (nodes.length === 0) return { x: 100, y: 200 };
  const last = nodes[nodes.length - 1];
  return findSmartPosition(last, size, nodes, groups);
}

function asLinks(value: unknown): NodeMeta['links'] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap(item => {
    if (!item || typeof item !== 'object') return [];
    const rec = item as { label?: unknown; url?: unknown };
    const label = typeof rec.label === 'string' ? rec.label : '';
    const url = typeof rec.url === 'string' ? rec.url : '';
    if (!label && !url) return [];
    return [{ label, url }];
  });
}

function nodeAgentView(node: Node) {
  return {
    id: node.id,
    label: node.label,
    type: node.type,
    position: node.position,
    catalogId: node.catalogId,
    linkedPageId: node.linkedPageId,
    meta: node.meta,
    description: node.meta?.description,
    owner: node.meta?.owner,
    status: node.meta?.status,
    links: node.meta?.links,
  };
}

function resolveCatalog(ref?: string): CatalogComponent | { error: string } {
  if (!ref) return { error: 'Missing catalog id or name' };
  const { catalog } = useDiagramStore.getState();
  const byId = catalog.find(entry => entry.id === ref);
  if (byId) return byId;
  const byName = catalog.filter(entry => entry.name.toLowerCase() === ref.toLowerCase());
  if (byName.length === 1) return byName[0];
  if (byName.length > 1) {
    return { error: `Ambiguous catalog name "${ref}" matches ${byName.length} entries. Use an id.` };
  }
  return { error: `Catalog entry not found: ${ref}` };
}

const query: Record<string, CommandHandler> = {
  getSummary() {
    const state = useDiagramStore.getState();
    return {
      diagramType: state.diagramType,
      activePage: state.pages.find(p => p.id === state.activePage)?.name ?? state.activePage,
      pageCount: state.pages.length,
      nodeCount: state.nodes.length,
      edgeCount: state.edges.length,
      participantCount: state.participants.length,
      messageCount: state.messages.length,
      nodes: state.nodes.map(n => ({
        id: n.id,
        label: n.label,
        type: n.type,
        position: n.position,
        linkedPageId: n.linkedPageId,
        catalogId: n.catalogId,
        description: n.meta?.description,
        owner: n.meta?.owner,
        status: n.meta?.status,
        links: n.meta?.links,
        sockets: {
          inputs: n.sockets.filter(s => s.type === 'input').length,
          outputs: n.sockets.filter(s => s.type === 'output').length,
        },
      })),
      edges: state.edges.map(e => {
        const src = state.nodes.find(n => n.id === e.sourceNodeId);
        const tgt = state.nodes.find(n => n.id === e.targetNodeId);
        return {
          id: e.id,
          sourceLabel: src?.label ?? e.sourceNodeId,
          targetLabel: tgt?.label ?? e.targetNodeId,
          label: e.label,
        };
      }),
      catalogCount: state.catalog.length,
      catalog: state.catalog.map(entry => ({
        id: entry.id,
        name: entry.name,
        type: entry.type,
        c4Level: entry.c4Level,
        views: countCatalogViews(state, entry.id),
      })),
      participants: state.participants.map(p => ({
        id: p.id,
        label: p.label,
        type: p.type,
        catalogId: p.catalogId,
        description: p.description,
        links: p.links,
      })),
      messages: state.messages.map(m => ({
        id: m.id,
        label: m.label,
        type: m.type,
        sourceId: m.sourceId,
        targetId: m.targetId,
        y: m.y,
      })),
    };
  },

  getNodes(params) {
    const { nodes } = useDiagramStore.getState();
    const filter = (params.filter as { type?: string; labelContains?: string } | undefined) ?? params;
    let result = nodes;
    const type = asString(filter.type);
    const labelContains = asString(filter.labelContains);
    if (type) result = result.filter(n => n.type === type);
    if (labelContains) {
      const lc = labelContains.toLowerCase();
      result = result.filter(n => n.label.toLowerCase().includes(lc));
    }
    return result.map(n => nodeAgentView(n));
  },

  findNode(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return null;
    const node = resolveNode(labelOrId);
    if (isResolveError(node)) return null;
    return {
      ...nodeAgentView(node),
      size: node.size,
      sockets: node.sockets.map(s => ({ id: s.id, type: s.type, label: s.label, order: s.order })),
    };
  },

  getEdges() {
    const { edges, nodes } = useDiagramStore.getState();
    return edges.map(e => ({
      id: e.id,
      sourceLabel: nodes.find(n => n.id === e.sourceNodeId)?.label ?? e.sourceNodeId,
      targetLabel: nodes.find(n => n.id === e.targetNodeId)?.label ?? e.targetNodeId,
      sourceSocketId: e.sourceSocketId,
      targetSocketId: e.targetSocketId,
      label: e.label,
      animated: e.animated,
      color: e.color,
      edgeType: e.edgeType,
    }));
  },

  getAIContext(params) {
    const userMessage = asString(params.userMessage) ?? asString(params.message) ?? '';
    const maxTokens = asNumber(params.maxTokens);
    return buildAIContext(userMessage, maxTokens);
  },
};

const flow: Record<string, CommandHandler> = {
  addNode(params) {
    const type = asString(params.type);
    if (!type) return { error: 'Missing node type' };
    const state = useDiagramStore.getState();
    const count = state.nodes.filter(n => n.type === type).length;
    let blueprint;
    try {
      blueprint = NodeFactory.create(type, count);
    } catch {
      return { error: `Unknown node type: ${type}` };
    }
    const position = asPoint(params.position) ?? defaultNodePosition(blueprint.size);
    const before = new Set(state.nodes.map(n => n.id));
    const color = asString(params.color);
    state.addNode({
      type,
      label: asString(params.label) ?? blueprint.label,
      position,
      size: blueprint.size,
      sockets: blueprint.sockets,
      data: {
        ...blueprint.data,
        ...(color ? { color } : {}),
      },
    });
    const newNode = useDiagramStore.getState().nodes.find(n => !before.has(n.id));
    return newNode ? { id: newNode.id } : { error: 'Node creation failed' };
  },

  updateNode(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing node label or id' };
    const node = resolveNode(labelOrId);
    if (isResolveError(node)) return node;
    const patch: { label?: string; position?: { x: number; y: number }; data?: typeof node.data } = {};
    const newLabel = asString(params.newLabel);
    if (newLabel !== undefined) patch.label = newLabel;
    const position = asPoint(params.position);
    if (position) patch.position = position;
    const color = asString(params.color);
    if (color !== undefined) patch.data = { ...node.data, color };
    useDiagramStore.getState().updateNode(node.id, patch);
    const metaPatch: Partial<NodeMeta> = {};
    const description = asString(params.description);
    if (description !== undefined) metaPatch.description = description;
    const owner = asString(params.owner);
    if (owner !== undefined) metaPatch.owner = owner;
    const status = asString(params.status);
    if (status !== undefined) metaPatch.status = status as NodeMeta['status'];
    const tooltip = asString(params.tooltip);
    if (tooltip !== undefined) metaPatch.tooltip = tooltip;
    const links = asLinks(params.links);
    if (links !== undefined) metaPatch.links = links;
    if (Object.keys(metaPatch).length > 0) {
      useDiagramStore.getState().updateNodeMeta(node.id, metaPatch);
    }
    return { ok: true as const };
  },

  removeNode(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing node label or id' };
    const node = resolveNode(labelOrId);
    if (isResolveError(node)) return node;
    useDiagramStore.getState().removeNode(node.id);
    return { removedId: node.id };
  },

  connectNodes(params) {
    const sourceRef = asString(params.sourceNode);
    const targetRef = asString(params.targetNode);
    if (!sourceRef || !targetRef) return { error: 'sourceNode and targetNode are required' };
    const state = useDiagramStore.getState();
    const srcNode = resolveNode(sourceRef);
    const tgtNode = resolveNode(targetRef);
    if (isResolveError(srcNode)) return { error: `Source node not found: ${sourceRef}` };
    if (isResolveError(tgtNode)) return { error: `Target node not found: ${targetRef}` };
    const srcSocketId = resolveSocket(srcNode, params.sourceSocket as number | string | undefined, 'output');
    const tgtSocketId = resolveSocket(tgtNode, params.targetSocket as number | string | undefined, 'input');
    if (!srcSocketId) return { error: `No output socket on source node "${sourceRef}"` };
    if (!tgtSocketId) return { error: `No input socket on target node "${targetRef}"` };
    const before = new Set(state.edges.map(e => e.id));
    state.addEdge({
      sourceNodeId: srcNode.id,
      sourceSocketId: srcSocketId,
      targetNodeId: tgtNode.id,
      targetSocketId: tgtSocketId,
      label: asString(params.label),
      animated: typeof params.animated === 'boolean' ? params.animated : undefined,
      color: asString(params.color),
      edgeType: params.edgeType as 'arrow' | 'no-arrow' | 'bidirectional' | undefined,
    });
    const newEdge = useDiagramStore.getState().edges.find(e => !before.has(e.id));
    return newEdge ? { id: newEdge.id } : { error: 'Edge not created (duplicate or invalid socket pair)' };
  },

  removeEdge(params) {
    const state = useDiagramStore.getState();
    let edgeId: string | undefined;
    const id = asString(params.id);
    if (id) {
      edgeId = state.edges.find(e => e.id === id)?.id;
    } else {
      const sourceRef = asString(params.sourceNode);
      const targetRef = asString(params.targetNode);
      if (sourceRef && targetRef) {
        const src = resolveNode(sourceRef);
        const tgt = resolveNode(targetRef);
        if (!isResolveError(src) && !isResolveError(tgt)) {
          edgeId = state.edges.find(e => e.sourceNodeId === src.id && e.targetNodeId === tgt.id)?.id;
        }
      }
    }
    if (!edgeId) return { error: 'Edge not found' };
    state.removeEdge(edgeId);
    return { removedId: edgeId };
  },

  updateEdge(params) {
    const id = asString(params.id);
    if (!id) return { error: 'Missing edge id' };
    const state = useDiagramStore.getState();
    if (!state.edges.find(e => e.id === id)) return { error: `Edge not found: ${id}` };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, labelOrId: _lid, ...updates } = params;
    state.updateEdge(id, updates as Parameters<typeof state.updateEdge>[1]);
    return { ok: true as const };
  },

  frameNodes(params) {
    // Visual frame only. Prefer createGroup for architecture subgraphs.
    const refs = asStringArray(params.labelOrIds);
    if (refs.length === 0) return { error: 'labelOrIds is required' };
    const state = useDiagramStore.getState();
    const nodeIds: string[] = [];
    for (const ref of refs) {
      const node = resolveNode(ref);
      if (isResolveError(node)) return node;
      nodeIds.push(node.id);
    }
    const targetNodes = state.nodes.filter(n => nodeIds.includes(n.id));
    if (targetNodes.length === 0) return { error: 'No valid nodes found' };

    const PADDING = 40;
    const HEADER = 32;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of targetNodes) {
      minX = Math.min(minX, n.position.x);
      minY = Math.min(minY, n.position.y);
      maxX = Math.max(maxX, n.position.x + n.size.width);
      maxY = Math.max(maxY, n.position.y + n.size.height);
    }

    const frameId = state.createFrame(
      { x: minX - PADDING, y: minY - PADDING - HEADER },
      { width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 + HEADER },
      nodeIds,
    );
    const label = asString(params.label);
    if (label) state.updateFrame(frameId, { label });
    return { frameId };
  },

  createGroup(params) {
    // Architecture subgraph with boundary sockets. This is the grouping API agents should use.
    const refs = asStringArray(params.labelOrIds);
    if (refs.length < 2) return { error: 'createGroup requires at least 2 nodes' };
    const nodeIds: string[] = [];
    for (const ref of refs) {
      const node = resolveNode(ref);
      if (isResolveError(node)) return node;
      nodeIds.push(node.id);
    }
    const id = useDiagramStore.getState().createGroup(nodeIds, asString(params.label));
    return id ? { id } : { error: 'Group creation failed' };
  },

  dissolveGroup(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing group node label or id' };
    const node = resolveNode(labelOrId);
    if (isResolveError(node)) return node;
    if (!node.childGraphId) return { error: `Node "${node.label}" is not a group` };
    useDiagramStore.getState().dissolveGroup(node.id);
    return { ok: true as const };
  },
};

const sequence: Record<string, CommandHandler> = {
  addParticipant(params) {
    const label = asString(params.label);
    if (!label) return { error: 'Missing participant label' };
    const state = useDiagramStore.getState();
    const before = new Set(state.participants.map(p => p.id));
    state.addParticipant({
      label,
      ...(asString(params.type) ? { type: asString(params.type) } : {}),
      ...(asString(params.color) ? { color: asString(params.color) } : {}),
    });
    const newP = useDiagramStore.getState().participants.find(p => !before.has(p.id));
    const x = asNumber(params.x);
    if (newP && x !== undefined) {
      useDiagramStore.getState().updateParticipant(newP.id, { x });
    }
    return { id: newP?.id ?? 'unknown' };
  },

  removeParticipant(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing participant label or id' };
    const p = resolveParticipant(labelOrId);
    if (isResolveError(p)) return p;
    useDiagramStore.getState().removeParticipant(p.id);
    return { ok: true as const };
  },

  getParticipants() {
    return useDiagramStore.getState().participants.map(p => ({
      id: p.id,
      label: p.label,
      type: p.type,
      color: p.color,
      order: p.order,
      x: p.x,
      catalogId: p.catalogId,
      description: p.description,
      links: p.links,
    }));
  },

  addMessage(params) {
    const srcRef = asString(params.sourceParticipant);
    const tgtRef = asString(params.targetParticipant);
    const label = asString(params.label);
    if (!srcRef || !tgtRef || !label) {
      return { error: 'sourceParticipant, targetParticipant, and label are required' };
    }
    const state = useDiagramStore.getState();
    const src = resolveParticipant(srcRef);
    const tgt = resolveParticipant(tgtRef);
    if (isResolveError(src)) return { error: `Source participant not found: ${srcRef}` };
    if (isResolveError(tgt)) return { error: `Target participant not found: ${tgtRef}` };
    const y = asNumber(params.y);
    const before = new Set(state.messages.map(m => m.id));
    state.addMessage({
      sourceId: src.id,
      targetId: tgt.id,
      label,
      type: (asString(params.type) as SequenceMessageType) ?? 'sync',
      ...(y !== undefined ? { y } : {}),
      ...(asString(params.color) ? { color: asString(params.color) } : {}),
    });
    const newMsg = useDiagramStore.getState().messages.find(m => !before.has(m.id));
    return newMsg ? { id: newMsg.id } : { error: 'Message creation failed' };
  },

  updateMessage(params) {
    const id = asString(params.id);
    if (!id) return { error: 'Missing message id' };
    const { messages, updateMessage } = useDiagramStore.getState();
    if (!messages.find(m => m.id === id)) return { error: `Message not found: ${id}` };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, labelOrId: _lid, ...updates } = params;
    updateMessage(id, updates as Parameters<typeof updateMessage>[1]);
    return { ok: true as const };
  },

  removeMessage(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing message label or id' };
    const { messages, removeMessage } = useDiagramStore.getState();
    const msg =
      messages.find(m => m.id === labelOrId) ??
      messages.filter(m => m.label.toLowerCase() === labelOrId.toLowerCase());
    const resolved = Array.isArray(msg) ? (msg.length === 1 ? msg[0] : null) : msg;
    if (!resolved) {
      return {
        error: Array.isArray(msg) && msg.length > 1
          ? `Ambiguous message label "${labelOrId}"`
          : `Message not found: ${labelOrId}`,
      };
    }
    removeMessage(resolved.id);
    return { ok: true as const };
  },

  getMessages() {
    const { messages, participants } = useDiagramStore.getState();
    return messages.map(m => ({
      id: m.id,
      label: m.label,
      type: m.type,
      sourceParticipant: participants.find(p => p.id === m.sourceId)?.label ?? m.sourceId,
      targetParticipant: participants.find(p => p.id === m.targetId)?.label ?? m.targetId,
      y: m.y,
      color: m.color,
    }));
  },

  addFragment(params) {
    const type = asString(params.type);
    const label = asString(params.label);
    if (!type || !label) return { error: 'Fragment type and label are required' };
    const state = useDiagramStore.getState();
    const participantIds = Array.isArray(params.participantIds)
      ? asStringArray(params.participantIds).map(ref => {
          const p = resolveParticipant(ref);
          return isResolveError(p) ? ref : p.id;
        })
      : state.participants.map(p => p.id);
    const before = new Set(state.fragments.map(f => f.id));
    state.addFragment({
      type: type as 'loop' | 'alt' | 'opt' | 'par',
      label,
      startY: asNumber(params.startY) ?? 0,
      endY: asNumber(params.endY) ?? 0,
      participantIds,
      altDividerY: asNumber(params.altDividerY),
      ...(asString(params.parentFragmentId) ? { parentFragmentId: asString(params.parentFragmentId) } : {}),
    });
    const newFrag = useDiagramStore.getState().fragments.find(f => !before.has(f.id));
    if (!newFrag) return { error: 'Fragment creation failed' };
    const contains = asStringArray(params.containsMessages);
    if (contains.length > 0) {
      const currentState = useDiagramStore.getState();
      const msgUpdates = contains.map(ref => {
        const m = currentState.messages.find(msg => msg.id === ref)
          ?? currentState.messages.find(msg => msg.label.toLowerCase() === ref.toLowerCase());
        return m ? { id: m.id, fragmentId: newFrag.id } : null;
      }).filter((u): u is { id: string; fragmentId: string } => u !== null);
      if (msgUpdates.length > 0) {
        useDiagramStore.getState().applySequenceLayoutPatch({
          messageFragmentUpdates: msgUpdates,
          recalcFragmentIds: [newFrag.id],
        });
      }
    }
    return { id: newFrag.id };
  },

  removeFragment(params) {
    const id = asString(params.id);
    if (!id) return { error: 'Missing fragment id' };
    const { fragments, removeFragment } = useDiagramStore.getState();
    if (!fragments.find(f => f.id === id)) return { error: `Fragment not found: ${id}` };
    removeFragment(id);
    return { ok: true as const };
  },

  getFragments() {
    const { fragments, participants } = useDiagramStore.getState();
    return fragments.map(f => ({
      id: f.id,
      type: f.type,
      label: f.label,
      startY: f.startY,
      endY: f.endY,
      altDividerY: f.altDividerY,
      parentFragmentId: f.parentFragmentId,
      participantLabels: f.participantIds.map(pid => participants.find(p => p.id === pid)?.label ?? pid),
      participantIds: f.participantIds,
    }));
  },

  updateFragment(params) {
    const id = asString(params.id);
    if (!id) return { error: 'Missing fragment id' };
    const { fragments, updateFragment } = useDiagramStore.getState();
    if (!fragments.find(f => f.id === id)) return { error: `Fragment not found: ${id}` };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, labelOrId: _lid, ...updates } = params;
    updateFragment(id, updates as Parameters<typeof updateFragment>[1]);
    return { ok: true as const };
  },

  insertMessage(params) {
    const srcRef = asString(params.sourceParticipant);
    const tgtRef = asString(params.targetParticipant);
    const label = asString(params.label);
    if (!srcRef || !tgtRef || !label) {
      return { error: 'sourceParticipant, targetParticipant, and label are required' };
    }
    const state = useDiagramStore.getState();
    const SPACING = asNumber(params.spacing) ?? 60;
    const src = resolveParticipant(srcRef);
    const tgt = resolveParticipant(tgtRef);
    if (isResolveError(src)) return { error: `Source participant not found: ${srcRef}` };
    if (isResolveError(tgt)) return { error: `Target participant not found: ${tgtRef}` };

    const sortedMsgs = [...state.messages].sort((a, b) => a.y - b.y);
    let insertY: number;
    const after = asString(params.after);
    const beforeRef = asString(params.before);
    if (after) {
      const ref = state.messages.find(m => m.id === after)
        ?? state.messages.find(m => m.label.toLowerCase() === after.toLowerCase());
      if (!ref) return { error: `Reference message not found: ${after}` };
      insertY = ref.y + SPACING;
    } else if (beforeRef) {
      const ref = state.messages.find(m => m.id === beforeRef)
        ?? state.messages.find(m => m.label.toLowerCase() === beforeRef.toLowerCase());
      if (!ref) return { error: `Reference message not found: ${beforeRef}` };
      insertY = ref.y - SPACING;
    } else {
      insertY = sortedMsgs.length > 0 ? sortedMsgs[sortedMsgs.length - 1].y + SPACING : 140;
    }

    const msgsToShift = sortedMsgs.filter(m => m.y >= insertY);
    if (msgsToShift.length > 0) {
      state.applySequenceLayoutPatch({
        messageYUpdates: msgsToShift.map(m => ({ id: m.id, y: m.y + SPACING })),
        sortMessages: true,
      });
    }

    const before = new Set(useDiagramStore.getState().messages.map(m => m.id));
    useDiagramStore.getState().addMessage({
      sourceId: src.id,
      targetId: tgt.id,
      label,
      type: (asString(params.type) as SequenceMessageType) ?? 'sync',
      y: insertY,
      ...(asString(params.color) ? { color: asString(params.color) } : {}),
    });
    const newMsg = useDiagramStore.getState().messages.find(m => !before.has(m.id));
    return newMsg ? { id: newMsg.id } : { error: 'Message insertion failed' };
  },
};

const pages: Record<string, CommandHandler> = {
  listPages() {
    const { pages: allPages, activePage } = useDiagramStore.getState();
    return allPages.map(p => ({
      id: p.id,
      name: p.name,
      description: p.description,
      diagramType: p.diagramType ?? 'flow',
      isActive: p.id === activePage,
    }));
  },

  addPage(params) {
    const name = asString(params.name);
    if (!name) return { error: 'Missing page name' };
    const diagramType = (asString(params.diagramType) as DiagramType | undefined) ?? 'flow';
    const state = useDiagramStore.getState();
    const before = new Set(state.pages.map(p => p.id));
    state.addPage(name, diagramType);
    const newPage = useDiagramStore.getState().pages.find(p => !before.has(p.id));
    return newPage ? { id: newPage.id } : { error: 'Page creation failed' };
  },

  switchPage(params) {
    const nameOrId = asString(params.nameOrId) ?? asString(params.labelOrId);
    if (!nameOrId) return { error: 'Missing page name or id' };
    const { pages: allPages, switchPage } = useDiagramStore.getState();
    const pg =
      allPages.find(p => p.id === nameOrId) ??
      allPages.find(p => p.name.toLowerCase() === nameOrId.toLowerCase());
    if (!pg) return { error: `Page not found: ${nameOrId}` };
    switchPage(pg.id);
    return { ok: true as const };
  },

  linkNodeToPage(params) {
    const nodeRef = asString(params.nodeLabelOrId);
    const pageRef = asString(params.pageNameOrId);
    if (!nodeRef || !pageRef) return { error: 'nodeLabelOrId and pageNameOrId are required' };
    const { pages: allPages, linkNodeToPage } = useDiagramStore.getState();
    const node = resolveNode(nodeRef);
    if (isResolveError(node)) return node;
    const pg =
      allPages.find(p => p.id === pageRef) ??
      allPages.find(p => p.name.toLowerCase() === pageRef.toLowerCase());
    if (!pg) return { error: `Page not found: ${pageRef}` };
    linkNodeToPage(node.id, pg.id);
    return { ok: true as const };
  },

  setPageDescription(params) {
    const nameOrId = asString(params.nameOrId) ?? asString(params.labelOrId);
    const description = asString(params.description) ?? '';
    if (!nameOrId) return { error: 'Missing page name or id' };
    const { pages: allPages, setPageDescription } = useDiagramStore.getState();
    const pg =
      allPages.find(p => p.id === nameOrId) ??
      allPages.find(p => p.name.toLowerCase() === nameOrId.toLowerCase());
    if (!pg) return { error: `Page not found: ${nameOrId}` };
    setPageDescription(pg.id, description);
    return { ok: true as const };
  },

  getPageDetail(params) {
    const nameOrId = asString(params.nameOrId) ?? asString(params.labelOrId);
    if (!nameOrId) return { error: 'Missing page name or id' };
    const state = useDiagramStore.getState();
    const pg =
      state.pages.find(p => p.id === nameOrId) ??
      state.pages.find(p => p.name.toLowerCase() === nameOrId.toLowerCase());
    if (!pg) return { error: `Page not found: ${nameOrId}` };

    const isActive = pg.id === state.activePage;
    const participants = isActive ? state.participants : (pg.participants ?? []);
    const messages = isActive ? state.messages : (pg.messages ?? []);
    const fragments = isActive ? state.fragments : (pg.fragments ?? []);

    return {
      id: pg.id,
      name: pg.name,
      description: pg.description,
      diagramType: pg.diagramType ?? 'flow',
      isActive,
      participants: participants.map(p => ({
        id: p.id,
        label: p.label,
        type: p.type,
        color: p.color,
        catalogId: p.catalogId,
        description: p.description,
        links: p.links,
      })),
      nodes: (isActive ? state.nodes : (pg.nodes ?? [])).map(n => nodeAgentView(n)),
      messages: messages.map(m => ({
        id: m.id,
        label: m.label,
        type: m.type,
        sourceParticipant: participants.find(p => p.id === m.sourceId)?.label ?? m.sourceId,
        targetParticipant: participants.find(p => p.id === m.targetId)?.label ?? m.targetId,
        y: m.y,
        fragmentId: m.fragmentId,
        ...(m.description ? { description: m.description } : {}),
        ...(m.payload ? { payload: m.payload } : {}),
        ...(m.constraints ? { constraints: m.constraints } : {}),
        ...(m.color ? { color: m.color } : {}),
      })),
      fragments: fragments.map(f => ({
        id: f.id,
        type: f.type,
        label: f.label,
        startY: f.startY,
        endY: f.endY,
        altDividerY: f.altDividerY,
        parentFragmentId: f.parentFragmentId,
        participantLabels: f.participantIds.map(pid => participants.find(p => p.id === pid)?.label ?? pid),
      })),
    };
  },
};

const diagram: Record<string, CommandHandler> = {
  async importFromMermaid(params) {
    const mmd = asString(params.mmd);
    if (!mmd) return { error: 'Missing mermaid source' };
    try {
      const subgraphMode = asString(params.subgraphMode) === 'groups'
        ? 'groups' as const
        : (useSettingsStore.getState().mermaidSubgraphMode === 'groups' ? 'groups' as const : 'frames' as const);
      const nodeStyle = asString(params.nodeStyle) === 'shapes'
        ? 'shapes' as const
        : (useSettingsStore.getState().mermaidNodeStyle === 'shapes' ? 'shapes' as const : 'nodes' as const);
      // Fit boxes to the size the destination page draws its labels at. A page
      // that is not a flow page yet will be a fresh one, which starts on the
      // app default, so nothing has to be created to answer this.
      const target = useDiagramStore.getState();
      const textScale = target.diagramType === 'flow'
        ? (target.pages.find(page => page.id === target.activePage)?.settings?.textScale ?? 1)
        : 1;
      const data = mermaidToDiagram(mmd.trim(), { subgraphMode, nodeStyle, textScale });
      const isSequence = mmd.trim().startsWith('sequenceDiagram');
      if (isSequence) {
        ensureActiveDiagramType('sequence', 'Sequence', true);
        useDiagramStore.getState().replaceSequenceContent({
          participants: data.participants,
          messages: data.messages,
          fragments: data.fragments,
          participantGroups: data.participantGroups,
        });
        return {
          ok: true as const,
          stats: {
            participants: data.participants.length,
            messages: data.messages.length,
            fragments: data.fragments.length,
          },
        };
      }
      // Import formed the graph; this lays it out, through the same call
      // Beautify makes. The two produce the same picture from the same graph
      // because there is only one layout path to produce it with.
      await autoLayout(data.nodes, data.edges, data.groups, { axis: data.rankAxis ?? 'ttb' });
      ensureActiveDiagramType('flow', 'Flow', false);
      useDiagramStore.getState().replaceActivePageFlow({
        nodes: data.nodes,
        edges: data.edges,
        groups: data.groups,
      });
      // Mermaid flowcharts are rectilinear, and only orthogonal edges follow the
      // route the layout drew for them; curves at this density read as spaghetti
      // no matter how they are nudged. That is a fact about the page, though,
      // not about any one edge - saying it here leaves every edge free to be
      // overridden on its own, and lets the whole page be switched to curves in
      // one place.
      const importedPage = useDiagramStore.getState();
      importedPage.setPageSettings(importedPage.activePage, { routingMode: 'orthogonal' });
      fitActiveFlowInWindow();
      return {
        ok: true as const,
        stats: { nodes: data.nodes.length, edges: data.edges.length, groups: data.groups.length },
      };
    } catch (e) {
      return { error: (e as Error)?.message ?? 'Failed to import Mermaid' };
    }
  },

  exportToMermaid() {
    try {
      const state = useDiagramStore.getState();
      if (state.diagramType === 'sequence') {
        const mermaid = diagramToMermaidSequence(
          state.participants,
          state.messages,
          state.fragments,
          state.participantGroups,
        );
        return { mermaid, type: 'sequence' };
      }
      const mermaid = diagramToMermaid(state.nodes, state.edges, state.groups, state.graphs);
      return { mermaid, type: 'flow' };
    } catch (e) {
      return { error: (e as Error)?.message ?? 'Failed to export Mermaid' };
    }
  },

  clearDiagram(params) {
    const diagramType = (asString(params.diagramType) as DiagramType | undefined) ?? 'flow';
    useDiagramStore.getState().clearDiagram(diagramType);
    return { ok: true as const };
  },

  setDiagramType(params) {
    const type = asString(params.type) ?? 'flow';
    useDiagramStore.getState().setDiagramType(type as DiagramType);
    return { ok: true as const };
  },

  getDiagramJson() {
    const state = useDiagramStore.getState();
    return saveToJSON({
      diagramType: state.diagramType,
      nodes: state.nodes,
      edges: state.edges,
      viewport: state.viewport,
      layers: state.layers,
      groups: state.groups,
      graphs: state.graphs,
      activeGraphId: state.activeGraphId,
      pages: state.pages,
      activePage: state.activePage,
      participants: state.participants,
      messages: state.messages,
      fragments: state.fragments,
      participantGroups: state.participantGroups,
      seqLifelineLength: state.seqLifelineLength,
      catalog: state.catalog,
    });
  },

  setDiagramJson(params) {
    const json = asString(params.json);
    if (!json) return { error: 'Missing json' };
    try {
      const data = loadFromJSON(json);
      useDiagramStore.getState().loadDiagram(data);
      return { ok: true as const };
    } catch (e) {
      return { error: (e as Error)?.message ?? 'Invalid JSON' };
    }
  },

  async autoLayoutFlow() {
    const state = useDiagramStore.getState();
    if (state.diagramType !== 'flow') return { error: 'autoLayoutFlow only applies to flow diagrams' };
    if (state.nodes.length === 0) return { error: 'No nodes to layout' };
    const windowSize = { width: window.innerWidth, height: window.innerHeight };
    const layout = await LayoutService.layout(
      state.nodes, state.edges, state.groups, state.viewport, windowSize,
    );
    useDiagramStore.getState().applyLayout(layout);
    return { ok: true as const, movedCount: Object.keys(layout.positions).length };
  },

  reflowMessages(params) {
    const options = (params.options as { startY?: number; spacing?: number } | undefined) ?? params;
    const state = useDiagramStore.getState();
    const sorted = [...state.messages].sort((a, b) => a.y - b.y);
    if (sorted.length === 0) return { ok: true as const, messageCount: 0 };
    const BASE_Y = asNumber(options.startY) ?? 140;
    const EXTRA = asNumber(options.spacing) ?? 8;
    let cursor = BASE_Y;
    const updates: Array<{ id: string; y: number }> = [];
    for (const msg of sorted) {
      const fp = estimateSequenceMessageFootprint(
        state.participants, msg.label, msg.sourceId, msg.targetId, { safeMargin: 4 },
      );
      cursor += fp.above;
      updates.push({ id: msg.id, y: cursor });
      cursor += fp.below + EXTRA;
    }
    state.applySequenceLayoutPatch({
      messageYUpdates: updates,
      sortMessages: true,
      recalcFragmentIds: state.fragments.map(f => f.id),
    });
    return { ok: true as const, messageCount: sorted.length };
  },

  recalcFragments(params) {
    const state = useDiagramStore.getState();
    const ids = asStringArray(params.fragmentIds);
    const target = ids.length > 0 ? ids : state.fragments.map(f => f.id);
    if (target.length === 0) return { ok: true as const };
    state.applySequenceLayoutPatch({ recalcFragmentIds: target });
    return { ok: true as const };
  },

  async exportToPng(params) {
    try {
      const options = (params.options as { allPages?: boolean } | undefined) ?? params;
      const allPages = options.allPages === true;
      if (allPages) {
        const results = await exportAllPagesToPng();
        if (!results.length) return { error: 'No pages to export' };
        return {
          pngData: results[0].pngData,
          fileName: results[0].fileName,
          pageCount: results.length,
        };
      }
      return await exportCurrentPageToPng();
    } catch (e) {
      return { error: (e as Error)?.message ?? 'Failed to export PNG' };
    }
  },
};

const catalogCommands: Record<string, CommandHandler> = {
  updateNodeMeta(params) {
    const labelOrId = asString(params.labelOrId);
    if (!labelOrId) return { error: 'Missing node label or id' };
    const node = resolveNode(labelOrId);
    if (isResolveError(node)) return node;
    const metaPatch: Partial<NodeMeta> = {};
    const description = asString(params.description);
    if (description !== undefined) metaPatch.description = description;
    const owner = asString(params.owner);
    if (owner !== undefined) metaPatch.owner = owner;
    const status = asString(params.status);
    if (status !== undefined) metaPatch.status = status as NodeMeta['status'];
    const tooltip = asString(params.tooltip);
    if (tooltip !== undefined) metaPatch.tooltip = tooltip;
    const links = asLinks(params.links);
    if (links !== undefined) metaPatch.links = links;
    if (Object.keys(metaPatch).length === 0) return { error: 'No metadata fields provided' };
    useDiagramStore.getState().updateNodeMeta(node.id, metaPatch);
    return { ok: true as const, catalogId: node.catalogId };
  },

  getCatalog() {
    const state = useDiagramStore.getState();
    return state.catalog.map(entry => ({
      id: entry.id,
      name: entry.name,
      type: entry.type,
      c4Level: entry.c4Level,
      description: entry.description,
      owner: entry.owner,
      status: entry.status,
      links: entry.links,
      tech: entry.tech,
      color: entry.color,
      views: countCatalogViews(state, entry.id),
    }));
  },

  updateCatalog(params) {
    const ref = asString(params.catalogId) ?? asString(params.labelOrId);
    const entry = resolveCatalog(ref);
    if ('error' in entry) return entry;
    const patch: Partial<CatalogComponent> = {};
    const name = asString(params.name);
    if (name !== undefined) patch.name = name;
    const description = asString(params.description);
    if (description !== undefined) patch.description = description;
    const owner = asString(params.owner);
    if (owner !== undefined) patch.owner = owner;
    const status = asString(params.status);
    if (status !== undefined) patch.status = status as CatalogComponent['status'];
    const type = asString(params.type);
    if (type !== undefined) patch.type = type;
    const color = asString(params.color);
    if (color !== undefined) patch.color = color;
    const tech = asString(params.tech);
    if (tech !== undefined) patch.tech = tech;
    const c4Level = asString(params.c4Level) as C4Level | undefined;
    if (c4Level) patch.c4Level = c4Level;
    const links = asLinks(params.links);
    if (links !== undefined) patch.links = links;
    useDiagramStore.getState().updateCatalog(entry.id, patch);
    return { ok: true as const, catalogId: entry.id };
  },

  placeCatalogInstance(params) {
    const ref = asString(params.catalogId) ?? asString(params.labelOrId);
    const entry = resolveCatalog(ref);
    if ('error' in entry) return entry;
    const position = asPoint(params.position);
    const createdId = useDiagramStore.getState().placeCatalogInstance(entry.id, position);
    if (!createdId) return { error: 'Failed to place catalog instance' };
    return { id: createdId, catalogId: entry.id };
  },

  createChildView(params) {
    const nodeRef = asString(params.nodeLabelOrId) ?? asString(params.labelOrId);
    if (!nodeRef) return { error: 'Missing nodeLabelOrId' };
    const node = resolveNode(nodeRef);
    if (isResolveError(node)) return node;
    const name = asString(params.name);
    const diagramType = asString(params.diagramType) as DiagramType | undefined;
    const pageId = useDiagramStore.getState().createChildView(node.id, {
      name,
      diagramType,
    });
    if (!pageId) return { error: 'Failed to create child view' };
    useDiagramStore.getState().switchPage(pageId);
    return { pageId, catalogId: node.catalogId };
  },

  createC4Workspace(params) {
    const systemName = asString(params.systemName) ?? 'System';
    return useDiagramStore.getState().createC4Workspace(systemName);
  },
};

export const handlers: Record<string, CommandHandler> = {
  ...query,
  ...flow,
  ...sequence,
  ...pages,
  ...diagram,
  ...catalogCommands,
};
